import { useRef, MutableRefObject, RefObject, useLayoutEffect, useCallback } from "react";
import { View, FlatList, ScrollView, LayoutChangeEvent, Platform } from "react-native";
import { FieldErrors, FieldValues, SubmitErrorHandler, FieldPath } from "react-hook-form";
import { isUndefined } from "lodash";

type PositionsRef<T> = { [key in keyof T]?: number };

const onLayout =
  <T,>(positionsRef: MutableRefObject<PositionsRef<T>>, names: Array<keyof T>) =>
  (event: LayoutChangeEvent) => {
    for (const name of names) {
      positionsRef.current[name] = event.nativeEvent.layout.y;
    }
  };

export type Scrollable = Pick<ScrollView, "scrollTo">;
type ScrollableRef = MutableRefObject<Scrollable | undefined>;

const onSubmitErrorWrapper =
  <T extends FieldValues>(defaultValues: T, scrollToField: (field: string) => void): SubmitErrorHandler<T> =>
  (errors: FieldErrors<T>) => {
    const firstError = Object.keys(defaultValues).find((field) => errors.hasOwnProperty(field)) || "";

    if (firstError) scrollToField(firstError);
  };

const scrollToFieldWrapper =
  <T extends FieldValues>(scrollViewRef: ScrollableRef, positionsRef: MutableRefObject<PositionsRef<T>>) =>
  (field: string) => {
    const offset = positionsRef.current[field];

    if (scrollViewRef.current && !isUndefined(offset)) {
      if (Platform.OS === "web") {
        window.scrollTo({
          top: offset,
          behavior: "smooth",
        });
      } else {
        scrollViewRef.current.scrollTo({ y: offset, animated: true });
      }
    }
  };

export function useErrorScroll<T extends FieldValues>({
  defaultValues,
  flatListRef,
}: {
  defaultValues: T;
  flatListRef?: RefObject<FlatList>;
}) {
  const scrollViewRef = useRef<Scrollable>();

  const positionsRef = useRef<PositionsRef<T>>({});

  useLayoutEffect(
    useCallback(() => {
      if (flatListRef?.current && !scrollViewRef.current) {
        scrollViewRef.current = flatListRef.current.getNativeScrollRef() as ScrollView;
      }
    }, [flatListRef, scrollViewRef])
  );

  const scrollToField = scrollToFieldWrapper(scrollViewRef, positionsRef);

  const onSubmitError = onSubmitErrorWrapper(defaultValues, scrollToField);

  // Dividers are required for the error scroll to work
  // Must be top level in the container - see AssessmentPane.tsx
  const Divider = ({
    fields = [],
    field,
    hidden = false,
    size = "medium",
  }: {
    fields?: Array<FieldPath<T>>;
    field?: FieldPath<T>;
    hidden?: boolean;
    size?: "small" | "medium" | "large";
  }) => (
    <View
      className={
        hidden
          ? ""
          : `border-sky-lighter ${Platform.OS === "web" ? "mx-5" : ""} ${
              size === "small" ? "border-t-[1px]" : size === "medium" ? "border-t-[6px]" : "border-t-[12px]"
            }`
      }
      onLayout={onLayout(positionsRef, field ? fields.concat(field) : fields)}
    />
  );

  return { Divider, scrollViewRef, onSubmitError, scrollToField };
}
